import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  MenuItem,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import ScrollableDialog from '../common/ScrollableDialog';
import PrimaryActionButton from '../common/PrimaryActionButton';
import SwitchField from '../common/SwitchField';
import SalovareSelectField from '../common/SalovareSelectField';
import { fieldLabelSx, inputWrapperSx } from '../common/formFieldStyles';
import {
  FRESHA_PERMISSION_OPTIONS,
  revalidateTeamMemberRow,
} from '../../utils/freshaEmployeeCsv';

const emptyDraft = {
  name: '',
  email: '',
  jobTitle: '',
  userPermission: 'Basic',
  disabled: false,
};

const inputStyle = (hasError = false) => ({
  width: '100%',
  padding: '12px 16px',
  border: `2px solid ${hasError ? '#f44336' : '#e0e0e0'}`,
  borderRadius: '8px',
  fontSize: '16px',
  fontFamily: 'inherit',
  outline: 'none',
  transition: 'border-color 0.3s ease',
  backgroundColor: '#fff',
  boxSizing: 'border-box',
});

const FixFailedImportTeamMembersDialog = ({
  open,
  onClose,
  rows,
  onApply,
}) => {
  const { t } = useTranslation();
  const [selectedRowNumber, setSelectedRowNumber] = useState(null);
  const [draft, setDraft] = useState(emptyDraft);
  const [fieldErrors, setFieldErrors] = useState([]);

  const failingRows = useMemo(() => rows.filter((row) => row.errors.length > 0), [rows]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const firstRow = failingRows[0];
    if (!firstRow) {
      setSelectedRowNumber(null);
      setDraft(emptyDraft);
      return;
    }

    setSelectedRowNumber(firstRow.rowNumber);
    setDraft({
      name: firstRow.name || '',
      email: firstRow.email || '',
      jobTitle: firstRow.jobTitle || '',
      userPermission: firstRow.userPermission || 'Basic',
      disabled: Boolean(firstRow.disabled),
    });
    setFieldErrors([]);
  }, [open, failingRows]);

  const selectedRow = failingRows.find((row) => row.rowNumber === selectedRowNumber);

  const handleApply = () => {
    if (!selectedRow) {
      return;
    }

    const nextRow = revalidateTeamMemberRow({
      ...selectedRow,
      name: draft.name.trim(),
      email: draft.email.trim(),
      jobTitle: draft.jobTitle.trim(),
      userPermission: draft.userPermission,
      disabled: Boolean(draft.disabled),
    });

    if (nextRow.errors.length > 0) {
      setFieldErrors(nextRow.errors);
      return;
    }

    onApply(nextRow);
    setFieldErrors([]);
  };

  return (
    <ScrollableDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      showCloseButton
      dividers
      title={t('dataImport.teamMembers.fixDialog.title')}
      actions={(
        <>
          <Button onClick={onClose} color="inherit">{t('common.cancel')}</Button>
          <PrimaryActionButton onClick={handleApply} disabled={!selectedRow}>
            {t('dataImport.teamMembers.fixDialog.apply')}
          </PrimaryActionButton>
        </>
      )}
    >
      {failingRows.length === 0 ? (
        <Typography>{t('dataImport.teamMembers.fixDialog.noFailures')}</Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {failingRows.map((row) => (
              <Button
                key={row.rowNumber}
                variant={row.rowNumber === selectedRowNumber ? 'contained' : 'outlined'}
                onClick={() => {
                  setSelectedRowNumber(row.rowNumber);
                  setDraft({
                    name: row.name || '',
                    email: row.email || '',
                    jobTitle: row.jobTitle || '',
                    userPermission: row.userPermission || 'Basic',
                    disabled: Boolean(row.disabled),
                  });
                  setFieldErrors([]);
                }}
                sx={{ textTransform: 'none' }}
              >
                {t('dataImport.teamMembers.fixDialog.rowLabel', { row: row.rowNumber, name: row.name || '—' })}
              </Button>
            ))}
          </Box>

          <Divider />

          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
            <Box>
              <Typography sx={fieldLabelSx}>{t('dataImport.teamMembers.fields.name')} *</Typography>
              <Box
                component="input"
                value={draft.name}
                onChange={(event) => setDraft((prev) => ({ ...prev, name: event.target.value }))}
                sx={{ ...inputStyle(fieldErrors.includes('dataImport.teamMembers.errors.nameRequired')), ...inputWrapperSx }}
              />
            </Box>
            <Box>
              <Typography sx={fieldLabelSx}>{t('dataImport.teamMembers.fields.email')} *</Typography>
              <Box
                component="input"
                value={draft.email}
                onChange={(event) => setDraft((prev) => ({ ...prev, email: event.target.value }))}
                sx={{
                  ...inputStyle(
                    fieldErrors.includes('dataImport.teamMembers.errors.emailRequired')
                    || fieldErrors.includes('dataImport.teamMembers.errors.emailInvalid'),
                  ),
                  ...inputWrapperSx,
                }}
              />
            </Box>
            <Box>
              <Typography sx={fieldLabelSx}>{t('dataImport.teamMembers.fields.jobTitle')}</Typography>
              <Box
                component="input"
                value={draft.jobTitle}
                onChange={(event) => setDraft((prev) => ({ ...prev, jobTitle: event.target.value }))}
                sx={{ ...inputStyle(), ...inputWrapperSx }}
              />
            </Box>
            <Box>
              <SalovareSelectField
                label={t('dataImport.teamMembers.fields.permission')}
                value={draft.userPermission}
                onChange={(event) => setDraft((prev) => ({ ...prev, userPermission: event.target.value }))}
              >
                {FRESHA_PERMISSION_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {t(option.roleLabelKey)}
                  </MenuItem>
                ))}
              </SalovareSelectField>
            </Box>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <SwitchField
                label={t('dataImport.teamMembers.fields.disabled')}
                checked={draft.disabled}
                onChange={(event) => setDraft((prev) => ({ ...prev, disabled: event.target.checked }))}
              />
            </Box>
          </Box>

          {fieldErrors.length > 0 ? (
            <Alert severity="error">
              {fieldErrors.map((key) => t(key)).join(' · ')}
            </Alert>
          ) : null}
        </Box>
      )}
    </ScrollableDialog>
  );
};

export default FixFailedImportTeamMembersDialog;
